import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import PhaseBadge from '../components/PhaseBadge';
import ProgramModal from '../components/ProgramModal';
import PasswordGate from '../../components/PasswordGate';
import { getEditPassword } from '../../services/auth';

const PHASE_OPTIONS = [
  { value: '', label: 'All Phases' },
  { value: 'preclinical', label: 'Preclinical' },
  { value: 'phase1', label: 'Phase 1' },
  { value: 'phase2', label: 'Phase 2' },
  { value: 'phase3', label: 'Phase 3' },
  { value: 'approved', label: 'Approved' },
  { value: 'discontinued', label: 'Discontinued' },
];

function PipelinePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [programs, setPrograms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [indications, setIndications] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [targets,    setTargets]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState(() => !!getEditPassword());
  const [showUnlockGate, setShowUnlockGate] = useState(false);

  const [filters, setFilters] = useState({
    company_id: searchParams.get('company_id') || '',
    indication_id: searchParams.get('indication_id') || '',
    phase: searchParams.get('phase') || '',
    modality: searchParams.get('modality') || '',
    target: searchParams.get('target') || '',
    q: searchParams.get('q') || '',
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/companies`),
      axios.get(`${API_BASE_URL}/indications`),
    ]).then(([compRes, indRes]) => {
      setCompanies(compRes.data?.companies || compRes.data || []);
      setIndications(indRes.data || []);
    }).catch(() => {});
  }, []);

  const fetchPrograms = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = { status: 'all' };
    if (filters.company_id)    params.company_id    = filters.company_id;
    if (filters.indication_id) params.indication_id = filters.indication_id;
    if (filters.phase)         params.phase         = filters.phase;
    if (filters.modality)      params.modality      = filters.modality;
    if (filters.target)        params.target        = filters.target;
    if (filters.q)             params.q             = filters.q;

    axios.get(`${API_BASE_URL}/programs`, { params })
      .then(res => {
        const data = res.data;
        setPrograms(Array.isArray(data) ? data : (data.programs || []));

        const list = Array.isArray(data) ? data : (data.programs || []);
        const mods = [...new Set(list.map(p => p.modality).filter(Boolean))].sort();
        setModalities(mods);
        const tgts = [...new Set(list.map(p => p.target).filter(Boolean))].sort();
        setTargets(tgts);
      })
      .catch(() => setError('Failed to load pipeline data.'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  function handleFilterChange(field, value) {
    setFilters(prev => ({ ...prev, [field]: value }));
  }

  function clearFilters() {
    setFilters({ company_id: '', indication_id: '', phase: '', modality: '', target: '', q: '' });
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div>
      {showAddModal && (
        <ProgramModal
          onClose={() => setShowAddModal(false)}
          onSaved={(id) => { setShowAddModal(false); navigate(`/pipeline/programs/${id}`); }}
        />
      )}
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">Pipeline</h1>
          <p className="page-subtitle">
            {loading ? 'Loading…' : `${programs.length} program${programs.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        {editMode ? (
          <button className="btn-primary" style={{ marginBottom: 2 }} onClick={() => setShowAddModal(true)}>
            + Add Program
          </button>
        ) : showUnlockGate ? (
          <PasswordGate onUnlocked={() => { setEditMode(true); setShowUnlockGate(false); }} />
        ) : (
          <button className="btn-secondary" style={{ marginBottom: 2 }} onClick={() => setShowUnlockGate(true)}>
            🔒 Unlock to add
          </button>
        )}
      </div>

      <div className="page-body">
        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search drug, target, indication…"
            value={filters.q}
            onChange={e => handleFilterChange('q', e.target.value)}
          />

          <select
            value={filters.company_id}
            onChange={e => handleFilterChange('company_id', e.target.value)}
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            value={filters.indication_id}
            onChange={e => handleFilterChange('indication_id', e.target.value)}
          >
            <option value="">All Indications</option>
            {indications.map(i => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>

          <select
            value={filters.phase}
            onChange={e => handleFilterChange('phase', e.target.value)}
          >
            {PHASE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={filters.modality}
            onChange={e => handleFilterChange('modality', e.target.value)}
          >
            <option value="">All Modalities</option>
            {modalities.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <select
            value={filters.target}
            onChange={e => handleFilterChange('target', e.target.value)}
          >
            <option value="">All Targets</option>
            {targets.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button className="filter-clear-btn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        {error && <div className="error-state">{error}</div>}

        {!error && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Drug Name</th>
                  <th>Brand Name</th>
                  <th>Company</th>
                  <th>Indication</th>
                  <th>Phase</th>
                  <th>Modality</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="table-empty">Loading…</td>
                  </tr>
                ) : programs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-empty">No programs match the current filters.</td>
                  </tr>
                ) : (
                  programs.map(program => (
                    <tr
                      key={program.id}
                      className="clickable-row"
                      onClick={() => navigate(`/pipeline/programs/${program.id}`)}
                    >
                      <td>
                        <strong>{program.drug_name || '—'}</strong>
                        {(program.drug_names || []).filter(d => d.type === 'dev_code' || d.type === 'synonym').length > 0 && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            {(program.drug_names || []).filter(d => d.type === 'dev_code' || d.type === 'synonym').map(d => d.name).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>{program.brand_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td>{program.company_name || program.company?.name || '—'}</td>
                      <td>{program.indication_name || program.indication?.name || '—'}</td>
                      <td><PhaseBadge phase={program.current_phase || program.trial_phase} /></td>
                      <td>{program.modality || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td>{[program.target, ...(program.secondary_targets || [])].filter(Boolean).join(' · ') || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PipelinePage;
